/**
 * V6-011 砺锋台 — 真实关卡加载器
 *
 * 从 LevelStore 加载 63 关（22 内置 + 41 用户），按 max_units_per_team 分 3 类
 * 缓存到模块级变量（避免重复 fetch）
 *
 * @see design/v6-sharpen/gdd-v6-010-sharpen.md
 * @date 2026-07-20
 */

import { createLevelStore } from '../../storage/level-store';
import { type LevelEntry, type OpenType, OPEN_TYPES, difficultyToRank } from './types';

/** 模块级缓存：key = "1v1"|"2v2"|"3v3"，value = 按难度排序的关卡列表 */
let _cache: Record<OpenType, LevelEntry[]> | null = null;
let _loading: Promise<Record<OpenType, LevelEntry[]>> | null = null;
let _lastStatus: LevelLoadStatus = 'loading';
let _lastError: string | undefined;
let _failedCount = 0;

export type LevelLoadStatus = 'loading' | 'ready' | 'empty' | 'error';

export interface LevelLoadStatusSnapshot {
  status: LevelLoadStatus;
  total: number;
  failed: number;
  errorMessage?: string;
}

/**
 * 加载所有关卡并按 max_units_per_team 分类
 * 结果缓存在模块级变量，多次调用返回同一份数据
 */
export async function loadAllLevels(): Promise<Record<OpenType, LevelEntry[]>> {
  if (_cache) return _cache;
  if (_loading) return _loading;

  _lastStatus = 'loading';
  _lastError = undefined;
  _failedCount = 0;
  _loading = doLoad();
  try {
    _cache = await _loading;
    const total = OPEN_TYPES.reduce((sum, type) => sum + _cache![type].length, 0);
    _lastStatus = total > 0 ? 'ready' : 'empty';
    return _cache;
  } catch (error) {
    _lastStatus = 'error';
    _lastError = error instanceof Error ? error.message : String(error);
    throw error;
  } finally {
    _loading = null;
  }
}

/** 获取最近一次加载状态，用于屏幕显示和诊断。 */
export function getLastLevelLoadStatus(): LevelLoadStatusSnapshot {
  const total = _cache
    ? OPEN_TYPES.reduce((sum, type) => sum + _cache![type].length, 0)
    : 0;
  return { status: _lastStatus, total, failed: _failedCount, errorMessage: _lastError };
}

/** 重新加载（清除缓存后，用于关卡增删后刷新） */
export function invalidateLevelCache(): void {
  _cache = null;
  _loading = null;
  _lastStatus = 'loading';
  _lastError = undefined;
  _failedCount = 0;
}

async function doLoad(): Promise<Record<OpenType, LevelEntry[]>> {
  const store = createLevelStore();
  const metas = await store.list();

  console.log(`[level-loader] 扫描到 ${metas.length} 个关卡，正在加载约束数据…`);

  const entries: LevelEntry[] = [];

  // 逐个 load 获取 map.constraints.max_units_per_team
  for (const meta of metas) {
    try {
      const full = await store.load(meta.levelId);
      const maxUnits = full.map?.constraints?.max_units_per_team ?? 1;
      const type: OpenType = maxUnits === 2 ? '2v2' : maxUnits === 3 ? '3v3' : '1v1';

      entries.push({
        id: meta.levelId,
        name: meta.name,
        difficulty: meta.difficulty,
        difficultyStars: meta.difficultyStars ?? 1,
        mapSize: meta.mapSize,
        maxUnitsPerTeam: maxUnits,
        rank: difficultyToRank(meta.difficulty),
        type,
      });
    } catch (e) {
      _failedCount++;
      console.warn(`[level-loader] 加载关卡 ${meta.levelId} 失败，跳过:`, e);
    }
  }

  // 按类型分组
  const result: Record<OpenType, LevelEntry[]> = { '1v1': [], '2v2': [], '3v3': [] };
  for (const e of entries) {
    result[e.type].push(e);
  }

  // 排序：按难度（tutorial < easy < normal < hard < expert），同难度按名称
  const order = ['tutorial', 'easy', 'normal', 'hard', 'expert'];
  for (const type of OPEN_TYPES) {
    result[type].sort((a, b) => {
      const da = order.indexOf(a.difficulty);
      const db = order.indexOf(b.difficulty);
      if (da !== db) return da - db;
      return a.name.localeCompare(b.name);
    });
  }

  console.log(
    `[level-loader] 分类完成: 1v1=${result['1v1'].length}, 2v2=${result['2v2'].length}, 3v3=${result['3v3'].length}`
  );

  return result;
}

/** 同步获取缓存（必须先 await loadAllLevels()），用于快速查询 */
export function getCachedLevels(): Record<OpenType, LevelEntry[]> | null {
  return _cache;
}
